import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import styles from "./EmailTemplate.module.scss";
import ConfirmDeleteModal from "@/components/ConfirmDeleteModal/ConfirmDeleteModal";

import { fetchEmailTemplate } from "@/store/slices/EmailTemplateSlice";
import { deleteEmailBYId } from "@/services/api/EmailTemplate";

import type { RootState } from "@/store/rootReducer";
import type { AppDispatch } from "@/store/store";
import { setModalOpen } from "@/store/slices/globalSlice";
import { MODAL_ID } from "@/lib/constants";
import EmailTemplateModal from "./Components/EmailTemplateModal";
import Table from "@/components/Table/Table";
import Pagination from "@/components/Table/Pagination";
import SearchBox from "@/components/SearchBox/SearchBox";
import PageLayout from "@/components/PageLayout/PageLayout";
import CopyIcon from "@/components/SVGComponents/CopyIcon";
import DeleteIcon from "@/components/SVGComponents/DeleteIcon";
import EditIcon from "@/components/SVGComponents/EditIcon";

const EmailTemplate: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const { profile } = useSelector((state: RootState) => state.user);
  const currentUserRole = profile?.role?.toUpperCase();

  useEffect(() => {
    if (profile && currentUserRole === "EDITOR") {
      navigate("/dashboard");
    }
  }, [profile, currentUserRole, navigate]);

  const { emailTemplate, totalRecords, loading } = useSelector(
    (state: RootState) => state.emailTemplate
  );

  const [openModal, setOpenModal] = useState(false);
  const [search, setSearch] = useState("");
  const [editId, setEditId] = useState<number | null>(null);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const perPage = 10;
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);

  // DELETE
  const handleDelete = async () => {
    try {
      if (!selectedTemplate?.emailTemplateId) return;

      await deleteEmailBYId(selectedTemplate.emailTemplateId);
      dispatch(setModalOpen({ id: null, data: null }));
      
      dispatch(
        fetchEmailTemplate({
          pageNo: page,
          perPage,
          search: debouncedSearch,
        })
      );
    } catch (error) {
      console.error("Delete failed", error);
    }
  };

  // SEARCH DEBOUNCE
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1); // Reset to page 1 on new search
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  // FETCH DATA
  useEffect(() => {
    dispatch(
      fetchEmailTemplate({
        pageNo: page,
        perPage,
        search: debouncedSearch,
      })
    );
  }, [dispatch, page, debouncedSearch]);

  const formatDate = (dateStr: string) => {
    if (!dateStr || dateStr === "N/A") return "Not modified";
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch (e) {
      return dateStr;
    }
  };

  const columns = useMemo(() => {
    const baseColumns = [
      {
        name: "Name",
        cell: (row: any) => (
          <div className={styles.templateName}>{row.name || "Unnamed Template"}</div>
        ),
        sortable: true,
        grow: 2,
      },
      {
        name: "Date modified",
        selector: (row: any) => formatDate(row.lastModifiedAt || row.modifiedDate),
        sortable: true,
      },
      {
        name: "Modified by",
        selector: (row: any) => row.modifiedByName || row.modifiedByUser || "System",
        sortable: true,
      }
    ];

    if (currentUserRole !== "VIEWER") {
      baseColumns.push({
        name: "Actions",
        cell: (row: any) => (
          <div className={styles.actionBtns} style={{ justifyContent: 'flex-end', display: 'flex', width: '100%' }}>
            <button
              className={styles.edit}
              title="Edit Template"
              onClick={() => {
                setEditId(row.emailTemplateId);
                setOpenModal(true);
              }}
            >
              <EditIcon/>
            </button>
            <button
              className={styles.deleteRow}
              title="Delete Template"
              onClick={() => {
                setSelectedTemplate(row);
                dispatch(
                  setModalOpen({
                    id: MODAL_ID.CONFIRM_DELETE,
                    data: { userName: row.name || "this template" },
                  })
                );
              }}
            >
              <DeleteIcon/>
            </button>
          </div>
        ),
        right: true,
        width: "120px",
      } as any);
    }

    return baseColumns;
  }, [currentUserRole, page, debouncedSearch, selectedTemplate, dispatch]);

  return (
    <PageLayout
      title="Email templates"
      headerAction={
        currentUserRole !== "VIEWER" ? (
          <button
            className={styles.newBtn}
            onClick={() => setOpenModal(true)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: 6}}>
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            New Email Template
          </button>
        ) : undefined
      }
      innerActions={
        <SearchBox
          value={search}
          onChange={(val: string) => setSearch(val)}
          // placeholder="Search templates..."
          width={240}
        />
      }
    >
      <div className={styles.tableWrapper}>
        <Table 
          columns={columns} 
          data={Array.isArray(emailTemplate) ? emailTemplate : []} 
          progressPending={loading}
        />
        <Pagination 
          totalCount={totalRecords}
          pageSelected={page}
          setPageSelected={setPage}
          rowsPerPageValue={perPage}
        />
      </div>

      <ConfirmDeleteModal
        deleteLabel="Email Template"
        handleDelete={handleDelete}
      />
      
      {openModal && (
        <EmailTemplateModal
          onClose={() => {
            setOpenModal(false);
            setEditId(null);
          }}
          editId={editId || undefined}
          refreshList={() =>
            dispatch(fetchEmailTemplate({ pageNo: page, perPage, search: debouncedSearch }))
          }
        />
      )}
    </PageLayout>
  );
};

export default EmailTemplate;